import {CENTRAL_QUARTER_PREFIX, COMMON_BUILDING_TOKENS, KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS} from '../../constants/tokens';
import {checkIfOwner} from '../gameState/checkIfOwner';
import {calculateCitiesCache} from '../gameState/calculateCitiesCache';

/**
 * ЗАДАНИЯ МИССИИ.
 *
 * Задание — условие, которое проверяется ПО СОСТОЯНИЮ партии после каждого
 * действия (тот же принцип, что у предикатов обучения: проверяем состояние, а
 * не событие, поэтому отмена действия безопасна).
 *
 * Главные задания (isMain) завершают миссию победой, когда выполнены ВСЕ.
 * Задания-провалы (isFailure) завершают миссию поражением.
 */

export const MISSION_OBJECTIVE_TYPES = {
  CAPTURE_CITY: 'capture_city',
  KEEP_CITY: 'keep_city',
  DESTROY_UNIT: 'destroy_unit',
  DESTROY_ALL_ENEMY_UNITS: 'destroy_all_enemy_units',
  CAPTURE_ALL_ENEMY_CITIES: 'capture_all_enemy_cities',
  REACH_WIN_POINTS: 'reach_win_points',
  REACH_COINS: 'reach_coins',
  BUILD_CENTRAL_QUARTER: 'build_central_quarter',
  BUILD_SPECIAL_BUILDING: 'build_special_building',
  CAPTURE_KEY_POINT: 'capture_key_point',
  UNIT_REACHES_HEX: 'unit_reaches_hex',
  UNITS_COUNT: 'units_count',
  UNITS_TOTAL_SIZE: 'units_total_size',
  DESTROY_BUILDING: 'destroy_building',
  LEAD_BY_WIN_POINTS_ROUNDS: 'lead_by_win_points_rounds',
  WIN_BY_WIN_POINTS_FINAL: 'win_by_win_points_final',
  SURVIVE_ROUNDS: 'survive_rounds',
  // Условия ПОРАЖЕНИЯ (полярность обратная: срабатывают, когда становятся
  // истинными) — их ставят с флагом isFailure
  LOSE_CITY: 'lose_city',
  LOSE_ALL_UNITS: 'lose_all_units',
};

// Человекочитаемые названия для панели редактора
export const MISSION_OBJECTIVE_TITLES = {
  [MISSION_OBJECTIVE_TYPES.CAPTURE_CITY]: 'Захватить город',
  [MISSION_OBJECTIVE_TYPES.KEEP_CITY]: 'Удержать город',
  [MISSION_OBJECTIVE_TYPES.DESTROY_UNIT]: 'Уничтожить отряд',
  [MISSION_OBJECTIVE_TYPES.DESTROY_ALL_ENEMY_UNITS]: 'Уничтожить все отряды противника',
  [MISSION_OBJECTIVE_TYPES.CAPTURE_ALL_ENEMY_CITIES]: 'Захватить все города противника',
  [MISSION_OBJECTIVE_TYPES.REACH_WIN_POINTS]: 'Набрать победные очки',
  [MISSION_OBJECTIVE_TYPES.REACH_COINS]: 'Накопить монеты',
  [MISSION_OBJECTIVE_TYPES.BUILD_CENTRAL_QUARTER]: 'Построить центральный квартал',
  [MISSION_OBJECTIVE_TYPES.BUILD_SPECIAL_BUILDING]: 'Построить специальное строение',
  [MISSION_OBJECTIVE_TYPES.CAPTURE_KEY_POINT]: 'Захватить ключевую точку',
  [MISSION_OBJECTIVE_TYPES.UNIT_REACHES_HEX]: 'Довести отряд до ячейки',
  [MISSION_OBJECTIVE_TYPES.UNITS_COUNT]: 'Иметь отрядов',
  [MISSION_OBJECTIVE_TYPES.UNITS_TOTAL_SIZE]: 'Набрать суммарный размер отрядов',
  [MISSION_OBJECTIVE_TYPES.DESTROY_BUILDING]: 'Разрушить строение',
  [MISSION_OBJECTIVE_TYPES.LEAD_BY_WIN_POINTS_ROUNDS]: 'Лидировать по очкам несколько раундов',
  [MISSION_OBJECTIVE_TYPES.WIN_BY_WIN_POINTS_FINAL]: 'Победить по очкам в последнем раунде',
  [MISSION_OBJECTIVE_TYPES.SURVIVE_ROUNDS]: 'Продержаться раунды',
  [MISSION_OBJECTIVE_TYPES.LOSE_CITY]: 'Потерять город (поражение)',
  [MISSION_OBJECTIVE_TYPES.LOSE_ALL_UNITS]: 'Потерять все отряды (поражение)',
};

/**
 * Какие параметры нужны каждому типу — по этому описанию редактор рисует
 * форму создания задания. kind: hex | unit | number | color | token
 */
export const MISSION_OBJECTIVE_PARAMS = {
  [MISSION_OBJECTIVE_TYPES.CAPTURE_CITY]: [
    {name: 'centralHexKey', kind: 'hex', label: 'Ячейка центрального квартала'},
  ],
  [MISSION_OBJECTIVE_TYPES.KEEP_CITY]: [
    {name: 'centralHexKey', kind: 'hex', label: 'Ячейка центрального квартала'},
  ],
  [MISSION_OBJECTIVE_TYPES.DESTROY_UNIT]: [
    {name: 'unitId', kind: 'unit', label: 'Отряд'},
  ],
  [MISSION_OBJECTIVE_TYPES.DESTROY_ALL_ENEMY_UNITS]: [
    {name: 'color', kind: 'color', label: 'Цвет противника'},
  ],
  [MISSION_OBJECTIVE_TYPES.CAPTURE_ALL_ENEMY_CITIES]: [
    {name: 'color', kind: 'color', label: 'Цвет противника'},
  ],
  [MISSION_OBJECTIVE_TYPES.REACH_WIN_POINTS]: [
    {name: 'amount', kind: 'number', label: 'Сколько очков'},
  ],
  [MISSION_OBJECTIVE_TYPES.REACH_COINS]: [
    {name: 'amount', kind: 'number', label: 'Сколько монет'},
  ],
  [MISSION_OBJECTIVE_TYPES.BUILD_CENTRAL_QUARTER]: [
    {name: 'hexKey', kind: 'hex', label: 'Ячейка'},
  ],
  [MISSION_OBJECTIVE_TYPES.BUILD_SPECIAL_BUILDING]: [
    {name: 'token', kind: 'token', label: 'Строение'},
    {name: 'hexKey', kind: 'hex', label: 'Ячейка (необязательно)', optional: true},
  ],
  [MISSION_OBJECTIVE_TYPES.CAPTURE_KEY_POINT]: [
    {name: 'hexKey', kind: 'hex', label: 'Ячейка ключевой точки'},
  ],
  [MISSION_OBJECTIVE_TYPES.UNIT_REACHES_HEX]: [
    {name: 'hexKey', kind: 'hex', label: 'Ячейка'},
  ],
  [MISSION_OBJECTIVE_TYPES.UNITS_COUNT]: [
    {name: 'amount', kind: 'number', label: 'Сколько отрядов'},
  ],
  [MISSION_OBJECTIVE_TYPES.UNITS_TOTAL_SIZE]: [
    {name: 'amount', kind: 'number', label: 'Суммарный размер'},
  ],
  [MISSION_OBJECTIVE_TYPES.DESTROY_BUILDING]: [
    {name: 'hexKey', kind: 'hex', label: 'Ячейка строения'},
  ],
  [MISSION_OBJECTIVE_TYPES.LEAD_BY_WIN_POINTS_ROUNDS]: [
    {name: 'rounds', kind: 'number', label: 'Сколько раундов подряд'},
  ],
  [MISSION_OBJECTIVE_TYPES.WIN_BY_WIN_POINTS_FINAL]: [],
  [MISSION_OBJECTIVE_TYPES.SURVIVE_ROUNDS]: [
    {name: 'rounds', kind: 'number', label: 'Сколько раундов'},
  ],
  [MISSION_OBJECTIVE_TYPES.LOSE_CITY]: [
    {name: 'centralHexKey', kind: 'hex', label: 'Ячейка центрального квартала'},
  ],
  [MISSION_OBJECTIVE_TYPES.LOSE_ALL_UNITS]: [],
};

const getPlayerByColor = (players, color) => players.find((player) => player.color === color) || null;

const getPlayerUnits = (units, player) => (player ? units.filter((unit) => unit.ownerId === player.id) : []);

/**
 * Проверка одного задания по состоянию партии.
 *
 * @param {Object} objective - {type, params, ...}
 * @param {Object} state - полный redux-стейт
 * @param {Object} context - {playerColor, progress} — за кого играет живой
 *   игрок и накопленный прогресс (для условий вида «N раундов подряд»)
 */
export const checkMissionObjective = (objective, state, {playerColor, progress = {}} = {}) => {
  const gameState = state.shared.gameState;
  const hexes = state.shared.hexes || {};
  const {players = [], units = [], specialBuildings = {}, round, finalRound} = gameState;
  const player = getPlayerByColor(players, playerColor);
  if (!player) return false;

  const params = objective.params || {};
  const citiesCache = calculateCitiesCache(hexes, specialBuildings);

  switch (objective.type) {
    case MISSION_OBJECTIVE_TYPES.CAPTURE_CITY:
    case MISSION_OBJECTIVE_TYPES.KEEP_CITY:
      return checkIfOwner(params.centralHexKey, playerColor, citiesCache);

    case MISSION_OBJECTIVE_TYPES.DESTROY_UNIT:
      return !units.some((unit) => unit.id === params.unitId);

    case MISSION_OBJECTIVE_TYPES.DESTROY_ALL_ENEMY_UNITS: {
      const enemy = getPlayerByColor(players, params.color);
      return Boolean(enemy) && getPlayerUnits(units, enemy).length === 0;
    }

    case MISSION_OBJECTIVE_TYPES.CAPTURE_ALL_ENEMY_CITIES:
      return (citiesCache[params.color] || []).length === 0;

    case MISSION_OBJECTIVE_TYPES.REACH_WIN_POINTS:
      return (player.winPoints || 0) >= Number(params.amount || 0);

    case MISSION_OBJECTIVE_TYPES.REACH_COINS:
      return (player.coins || 0) >= Number(params.amount || 0);

    case MISSION_OBJECTIVE_TYPES.BUILD_CENTRAL_QUARTER:
      return hexes[params.hexKey] === CENTRAL_QUARTER_PREFIX + playerColor;

    case MISSION_OBJECTIVE_TYPES.BUILD_SPECIAL_BUILDING: {
      if (params.hexKey) {
        return specialBuildings[params.hexKey] === params.token
          && checkIfOwner(params.hexKey, playerColor, citiesCache);
      }
      return Object.entries(specialBuildings).some(([hexKey, token]) => (
        token === params.token && checkIfOwner(hexKey, playerColor, citiesCache)
      ));
    }

    case MISSION_OBJECTIVE_TYPES.CAPTURE_KEY_POINT: {
      const token = hexes[params.hexKey];
      const isKeyPoint = Object.values(KEY_POINT_TOKENS).includes(token);
      return isKeyPoint && checkIfOwner(params.hexKey, playerColor, citiesCache);
    }

    case MISSION_OBJECTIVE_TYPES.UNIT_REACHES_HEX:
      return getPlayerUnits(units, player).some((unit) => unit.hexKey === params.hexKey);

    case MISSION_OBJECTIVE_TYPES.UNITS_COUNT:
      return getPlayerUnits(units, player).length >= Number(params.amount || 0);

    case MISSION_OBJECTIVE_TYPES.UNITS_TOTAL_SIZE:
      return getPlayerUnits(units, player)
        .reduce((sum, unit) => sum + (unit.size || 0), 0) >= Number(params.amount || 0);

    case MISSION_OBJECTIVE_TYPES.DESTROY_BUILDING: {
      // Разрушенное обычное строение движок превращает в дорогу (не в пустой
      // гекс), а спецстроение просто снимает — учитываем оба исхода
      const hexToken = hexes[params.hexKey];
      const isHexCleared = !hexToken || hexToken === COMMON_BUILDING_TOKENS.ROAD;
      return isHexCleared && !specialBuildings[params.hexKey];
    }

    case MISSION_OBJECTIVE_TYPES.LEAD_BY_WIN_POINTS_ROUNDS:
      return (progress.leadingRounds || 0) >= Number(params.rounds || 0);

    case MISSION_OBJECTIVE_TYPES.WIN_BY_WIN_POINTS_FINAL: {
      const isFinalRound = finalRound ? round >= finalRound : false;
      if (!isFinalRound) return false;
      return players.every((other) => other.color === playerColor
        || (other.winPoints || 0) <= (player.winPoints || 0));
    }

    case MISSION_OBJECTIVE_TYPES.SURVIVE_ROUNDS:
      return round >= Number(params.rounds || 0);

    // Условия поражения: истинны ровно тогда, когда беда случилась
    case MISSION_OBJECTIVE_TYPES.LOSE_CITY:
      return !checkIfOwner(params.centralHexKey, playerColor, citiesCache);

    case MISSION_OBJECTIVE_TYPES.LOSE_ALL_UNITS:
      return getPlayerUnits(units, player).length === 0;

    default:
      console.error('[Mission] Неизвестный тип задания:', objective.type);
      return false;
  }
};

/** Лидирует ли игрок по очкам прямо сейчас — для LEAD_BY_WIN_POINTS_ROUNDS */
export const checkIfLeadingByWinPoints = (state, playerColor) => {
  const players = state.shared.gameState.players || [];
  const player = getPlayerByColor(players, playerColor);
  if (!player) return false;

  return players.every((other) => other.color === playerColor
    || (other.winPoints || 0) < (player.winPoints || 0));
};

/** Текст задания для панели: пользовательский текст либо автоописание */
export const getMissionObjectiveText = (objective) => {
  if (objective.text) return objective.text;

  const params = objective.params || {};
  const title = MISSION_OBJECTIVE_TITLES[objective.type] || 'Задание';

  switch (objective.type) {
    case MISSION_OBJECTIVE_TYPES.CAPTURE_CITY:
    case MISSION_OBJECTIVE_TYPES.KEEP_CITY:
      return title + ' (' + params.centralHexKey + ')';
    case MISSION_OBJECTIVE_TYPES.REACH_WIN_POINTS:
    case MISSION_OBJECTIVE_TYPES.REACH_COINS:
    case MISSION_OBJECTIVE_TYPES.UNITS_COUNT:
    case MISSION_OBJECTIVE_TYPES.UNITS_TOTAL_SIZE:
      return title + ': ' + params.amount;
    case MISSION_OBJECTIVE_TYPES.LEAD_BY_WIN_POINTS_ROUNDS:
    case MISSION_OBJECTIVE_TYPES.SURVIVE_ROUNDS:
      return title + ': ' + params.rounds;
    case MISSION_OBJECTIVE_TYPES.BUILD_CENTRAL_QUARTER:
    case MISSION_OBJECTIVE_TYPES.CAPTURE_KEY_POINT:
    case MISSION_OBJECTIVE_TYPES.UNIT_REACHES_HEX:
    case MISSION_OBJECTIVE_TYPES.DESTROY_BUILDING:
      return title + ' (' + params.hexKey + ')';
    default:
      return title;
  }
};

/** Все ли главные задания выполнены */
export const checkIfAllMainObjectivesCompleted = (objectives, completedIds) => {
  const mainObjectives = (objectives || []).filter((objective) => objective.isMain && !objective.isFailure);
  return mainObjectives.length > 0
    && mainObjectives.every((objective) => completedIds.includes(objective.id));
};

/** Провалено ли хоть одно условие поражения */
export const findFailedObjective = (objectives, completedIds) => (objectives || [])
  .find((objective) => objective.isFailure && completedIds.includes(objective.id)) || null;

// Строения, которые можно требовать построить (для формы редактора)
export const MISSION_SPECIAL_BUILDING_OPTIONS = Object.values(SPECIAL_BUILDING_TOKENS);
